import {
	Dialog,
	IconButton,
	Input,
	LuIcon,
	radiusCva,
	Spinner,
	stopPropagation,
	Text,
	useProvider,
} from "@overmap-ai/blocks"
import { type GeoImage } from "@overmap-ai/common"
import { selectGeoImageById } from "@overmap-ai/core"
import { ImageFile } from "@overmap-ai/forms"
import { cx } from "class-variance-authority"
import { type FocusEventHandler, memo, useCallback } from "react"
import { twMerge } from "tailwind-merge"

import { useBlurInput, useFetchFileFromFileModel } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import type { ExtendableDialogProps } from "./typings"

interface GeoImageDetailsDialogProps extends ExtendableDialogProps {
	geoImageId: GeoImage["uuid"]
}

export const GeoImageDetailsDialog = memo<GeoImageDetailsDialogProps>((props) => {
	const {
		children,
		open,
		defaultOpen,
		onOpenChange, // oxlint-disable-line unbound-method
		modal,
		geoImageId,
		...rest
	} = props

	const geoImage = useAppSelector(selectGeoImageById(geoImageId))
	const { radius } = useProvider()

	if (!geoImage) {
		throw new Error(`GeoImage with id ${geoImageId} not found in store.`)
	}

	const [file, error] = useFetchFileFromFileModel(geoImage, sdk.geoImages)

	const handleTitleInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			void sdk.geoImages.update({ uuid: geoImage.uuid, title: e.target.value })
		},
		[geoImage.uuid],
	)

	const titleInputProps = useBlurInput<HTMLInputElement>(geoImage.title ?? "", handleTitleInputBlur, true)

	return (
		<Dialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
			{!!children && <Dialog.Trigger asChild>{children}</Dialog.Trigger>}
			<Dialog.Overlay onClick={stopPropagation} />
			<Dialog.Content onClick={stopPropagation} {...rest}>
				<div className="flex justify-between gap-2 pb-2">
					<Dialog.Title>
						<Text size="lg">Image details</Text>
					</Dialog.Title>

					<Dialog.Close asChild>
						<IconButton variant="ghost" accentColor="base">
							<LuIcon icon={modelIconMapping.close} />
						</IconButton>
					</Dialog.Close>
				</div>

				<div className="flex w-full flex-col gap-2 py-1">
					<div
						className={twMerge(
							cx("h-[300px] w-full overflow-hidden", radiusCva({ radius, maxLarge: true })),
						)}
					>
						<div className="flex grow items-center justify-center overflow-hidden bg-clip-padding">
							{!!file && !error ? (
								<ImageFile className="max-w-full object-cover" file={file} />
							) : !error ? (
								<Spinner />
							) : (
								<LuIcon icon={modelIconMapping.alertCircle} />
							)}
						</div>
					</div>
					<Input.Root variant="outline">
						<Input.Field placeholder="Enter a title" {...titleInputProps} />
					</Input.Root>
				</div>
			</Dialog.Content>
		</Dialog.Root>
	)
})
GeoImageDetailsDialog.displayName = "GeoImageDetailsDialog"
